import { SelectTescomaPointPopupProps } from 'components/Blocks/SelectPickupPointPopup/SelectTescomaPointPopup';
import Button from 'components/Forms/Button/Button';
import { useSettings } from 'connectors/Settings/Settings';
import { getLastDayFromPersonalPickupString } from 'helpers/getLastDayFromPersonalPickupString';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import Trans from 'next-translate/Trans';
import dynamic from 'next/dynamic';
import { RefObject, useCallback, useEffect, useState } from 'react';
import * as smoothscroll from 'smoothscroll-polyfill';
import { usePersistStore } from 'store/usePersistStore';
import { twJoin } from 'tailwind-merge';
import { AvailabilityStatusEnum, StoreListAvailabilityStatusEnum } from 'types/availability';
import { ProductDetailType, VariantDetailType } from 'types/product';
import { SearchedStoreType } from 'types/store';

const SelectTescomaPointPopup = dynamic<SelectTescomaPointPopupProps>(() =>
    import('components/Blocks/SelectPickupPointPopup/SelectTescomaPointPopup').then(
        (component) => component.SelectTescomaPointPopup,
    ),
);

type ProductDetailAvailabilityProps = {
    product: ProductDetailType | VariantDetailType;
    scrollTarget: RefObject<HTMLUListElement>;
};

const changeDefaultStoreTwClass = 'cursor-pointer text-black underline';

const ProductDetailAvailability: FC<ProductDetailAvailabilityProps> = ({ product }) => {
    const testIdentifier = 'pages-productdetail-productdetailavailability';
    const t = useTypedTranslationFunction();
    const settings = useSettings();

    const { updateDefaultStoreUuidState, updateUserState } = usePersistStore((s) => s);
    const defaultStoreAvailability = product.defaultStoreAvailability;
    const isElectronicGiftVoucher = product.isElectronicGiftVoucher;
    const [popupVisible, setPopupVisible] = useState(false);
    const openPopupHandler = useCallback(() => setPopupVisible(true), [setPopupVisible]);
    const closePopupHandler = useCallback(() => setPopupVisible(false), [setPopupVisible]);

    const changeStoreHandler = useCallback(
        (store: SearchedStoreType | null) => {
            if (store !== null) {
                updateDefaultStoreUuidState(store.uuid);
                updateUserState({ selectedStoreUuid: store.uuid });
            }

            closePopupHandler();
        },
        [updateDefaultStoreUuidState, updateUserState, closePopupHandler],
    );

    useEffect(() => {
        smoothscroll.polyfill();
    }, []);

    const isProductInStock = product.availability.status === AvailabilityStatusEnum.inStock;
    const isInCentralStoreWithEnabledReservations = product.deliveryDaysFromCentralStore !== null && isProductInStock;
    const isInCentralStoreWithEnabledReservationsAndNowhereElse =
        isInCentralStoreWithEnabledReservations && product.availableStoresCount === 0;
    const count = product.availableStoresCount;
    const { days, daysCount } = getLastDayFromPersonalPickupString(product.deliveryDaysFromCentralStore);

    const getStoreAvailabilityForDefaultStore = (
        inStock: boolean,
        inCentralStore: boolean,
        storeName: string,
        storesCount: number,
        deliveryDays: number,
    ) => {
        if (inStock) {
            return (
                <>
                    {`${t('Available')} `}
                    <span className={changeDefaultStoreTwClass} onClick={openPopupHandler}>
                        {t('in store {{ store }}', { store: storeName })}
                    </span>
                </>
            );
        }

        if (inCentralStore) {
            return (
                <>
                    {t('Personal pickup in {{ days }} days', { days, count: deliveryDays })}
                    <span className={changeDefaultStoreTwClass} onClick={openPopupHandler}>
                        &nbsp;
                        {t('in store {{ store }}', { store: storeName })}
                    </span>
                </>
            );
        }

        return (
            <span className="text-grayDarker">
                <Trans
                    i18nKey="Unavailable in store <link>{{ store }}</link>"
                    components={{ link: <span className={changeDefaultStoreTwClass} onClick={openPopupHandler} /> }}
                    values={{ store: storeName }}
                />
            </span>
        );
    };

    const productDetailAvailabilityInfoTwClass = twJoin(
        'text-small font-bold leading-6',
        product.availableStoresCount > 0 ||
            defaultStoreAvailability?.availabilityStatus === StoreListAvailabilityStatusEnum.inStock
            ? 'text-green'
            : 'text-grayDarker',
    );

    return (
        <div className="mb-5" data-testid={testIdentifier}>
            <div
                className={twJoin(
                    'text-h6 font-bold leading-6 hover:no-underline',
                    isProductInStock && 'text-green',
                    product.availability.status === AvailabilityStatusEnum.outOfStock && 'text-grayDarker',
                )}
            >
                {isProductInStock ? t('Available in eshop') : t('Unavailable in eshop')}
            </div>
            {defaultStoreAvailability === null && !isElectronicGiftVoucher && (
                <div
                    className={productDetailAvailabilityInfoTwClass}
                    data-testid={testIdentifier + '-in-stock-at-stores'}
                >
                    {isInCentralStoreWithEnabledReservationsAndNowhereElse ? (
                        <>
                            {t('Available in')}{' '}
                            <span className={changeDefaultStoreTwClass} onClick={openPopupHandler}>
                                {t('(in) stores')}
                            </span>{' '}
                            {t('in {{ count }} days', { count: daysCount, days })}
                        </>
                    ) : (
                        <>
                            {count === 0 && settings?.isAnyStockStore && t('Unavailable in stores')}
                            {count !== 0 && `${t('Available')} `}
                            {count !== 0 && (
                                <span className={changeDefaultStoreTwClass} onClick={openPopupHandler}>
                                    {t('in {{ count }} stores', { count })}
                                </span>
                            )}
                        </>
                    )}
                </div>
            )}

            {defaultStoreAvailability !== null && (
                <div className={productDetailAvailabilityInfoTwClass} data-testid={testIdentifier + '-default-store'}>
                    {getStoreAvailabilityForDefaultStore(
                        [
                            StoreListAvailabilityStatusEnum.inStock,
                            StoreListAvailabilityStatusEnum.inStockReservationDisabled,
                        ].includes(defaultStoreAvailability.availabilityStatus),
                        isInCentralStoreWithEnabledReservations,
                        defaultStoreAvailability.storeName,
                        product.availableStoresCount,
                        daysCount,
                    )}
                </div>
            )}

            {popupVisible && (
                <SelectTescomaPointPopup
                    popupTitle={t('Choose your Tescoma store')}
                    productDetail={product}
                    closePopupHandler={closePopupHandler}
                    placement="product-detail"
                    renderChangePickupPointButton={(store) => (
                        <Button
                            type="button"
                            size="small"
                            variant="primary"
                            onClick={() => changeStoreHandler(store as SearchedStoreType)}
                            isDisabled={false}
                        >
                            {t('Choose store')}
                        </Button>
                    )}
                />
            )}
        </div>
    );
};

export default ProductDetailAvailability;
